<template>
  <nav class="oc-tabbar" role="navigation" aria-label="Navigation principale">
    <RouterLink
      v-for="tab in tabs"
      :key="tab.key"
      :to="tab.to"
      class="oc-tab"
      :class="{ 'is-active': tab.key === activeKey }"
      :aria-current="tab.key === activeKey ? 'page' : undefined"
    >
      <!-- Icônes pleines, comme dans les maquettes : à 22px, un tracé plein
           se lit mieux qu'un contour, et l'état actif ressort davantage. -->
      <svg class="oc-tab__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path v-if="tab.key === 'home'" d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
        <path v-else-if="tab.key === 'offers'" d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14z" />
        <path v-else-if="tab.key === 'gallery'" d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
        <path v-else d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
      </svg>
      <span class="oc-tab__label">{{ tab.label }}</span>
    </RouterLink>
  </nav>
</template>

<script>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

/**
 * Barre d'onglets de l'application mobile.
 *
 * Remplace le menu hamburger + tiroir latéral hérités du site web, qui
 * demandaient deux gestes et une visée en haut de l'écran pour changer de
 * section. Les quatre destinations principales sont ici atteignables d'un
 * seul geste du pouce.
 *
 * Les icônes sont des SVG en ligne, et non des glyphes Font Awesome : la
 * feuille Font Awesome est chargée depuis un CDN, donc absente au premier
 * lancement hors-ligne. La navigation de l'app ne peut pas dépendre du réseau.
 *
 * Les cibles sont exprimées par NOM de route, jamais par chemin littéral :
 * le header web pointe vers « /Gallery » et « /Contact » alors que le routeur
 * déclare « /gallery » et « /contact », et ces liens retombent silencieusement
 * sur l'accueil. Les noms sont insensibles à ce piège.
 */
export default {
  name: 'OcTabBar',

  setup() {
    const route = useRoute();
    const store = useStore();

    const isLoggedIn = computed(() => store.state.auth.user !== null);

    const tabs = computed(() => [
      { key: 'home', label: 'Accueil', to: { name: 'HomePage' } },
      { key: 'offers', label: 'Activités', to: { name: 'OffersList' } },
      { key: 'gallery', label: 'Galerie', to: { name: 'Gallery' } },
      {
        key: 'account',
        label: isLoggedIn.value ? 'Compte' : 'Connexion',
        to: { name: isLoggedIn.value ? 'AccountPage' : 'LoginPage' },
      },
    ]);

    // Un onglet reste allumé sur les écrans qui lui appartiennent : ouvrir une
    // fiche d'offre depuis la liste ne doit pas éteindre l'onglet Activités.
    const SECTIONS = {
      offers: ['OffersList', 'OfferDetailsPage', 'OfferDetailsPageV2', 'ConfirmationPage', 'BookingSuccess'],
      gallery: ['Gallery'],
      account: ['AccountPage', 'Profile', 'LoginPage', 'MyOffers', 'ForgotPasswordPage', 'ResetPasswordPage', 'Contact'],
      home: ['HomePage'],
    };

    const activeKey = computed(() => {
      const name = route.name;
      const hit = Object.keys(SECTIONS).find((key) => SECTIONS[key].includes(name));
      return hit || null;
    });

    return { tabs, activeKey };
  },
};
</script>
